import LFCChartsComboHH from '_components/charts/LFCChartsComboHH';
import {ProgressAria} from '_components/ProgressAria';
import {getLFCData} from '_logics/LFCUtil';
import axios, {CancelTokenSource} from 'axios';
import dayjs from 'dayjs';
import {isEmpty} from 'lodash';
import {useSnackbar} from 'notistack';
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {green, red} from '@mui/material/colors';

import {SearchConditions} from '../types';

interface PerformanceAndDefectRateChartProps {
  searchConditions: SearchConditions | null;
}

const PerformanceAndDefectRateChart: React.FC<PerformanceAndDefectRateChartProps> = ({
  searchConditions
}) => {
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const [data60352, setData60352] = useState([]);
  const cancelSource = useRef<CancelTokenSource | null>(null);
  const [fixPeriod, setFixPeriod] = useState<{
    startDate: dayjs.Dayjs;
    endDate: dayjs.Dayjs;
  }>({startDate: dayjs(), endDate: dayjs()});
  const [startProcess, setStartProcess] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      // アンマウント処理
      if (cancelSource.current) cancelSource.current.cancel('req cancel');
    };
  }, []);

  useEffect(() => {
    if (searchConditions === null) return;

    if (cancelSource.current) cancelSource.current.cancel('req cancel');

    // 対象期間算出
    const now = dayjs();
    let fromDt = dayjs(`${now.format('YYYY-MM-DD')} ${searchConditions.select_time}`);

    // 稼働開始時刻より前の場合、前日の同時刻を開始時刻とする。
    if (searchConditions.select_time > now.format('HH:mm')) {
      // 稼働開始時刻
      fromDt = fromDt.add(-1, 'd');
    }
    const toDt = fromDt.add(1, 'd');

    setFixPeriod({startDate: fromDt, endDate: toDt});

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();
    getLFCData({
      snack: enqueueSnackbar,
      sql_id: 60352,
      parameters: {
        select_datetime_from: fromDt.format(),
        select_datetime_to: toDt.format(),
        work: !isEmpty(searchConditions.work) ? '{' + searchConditions.work.join(',') + '}' : null,
        deviceid: !isEmpty(searchConditions.deviceid)
          ? '{' + searchConditions.deviceid.join(',') + '}'
          : null,
        dongleid: !isEmpty(searchConditions.dongleid)
          ? '{' + searchConditions.dongleid.join(',') + '}'
          : null
      },
      ds_state: setData60352,
      name: `${t('実績数と不良率')}`,
      cancelToken: cancelSource.current.token,
      t
    }).finally(() => {
      setStartProcess(false);
    });
  }, [searchConditions, enqueueSnackbar, t]);

  return (
    <ProgressAria startProcess={startProcess}>
      <LFCChartsComboHH
        title={t('実績数と不良率')}
        source={data60352}
        x={fixPeriod}
        yLeft={{
          type: 'bar',
          name: `${t('実績数')}`,
          dsColumn: 'total_count'
        }}
        yRight={{
          type: 'line',
          name: `${t('不良率')}(%)`,
          dsColumn: 'ng_par',
          markPoint: {
            data: [
              {type: 'min', itemStyle: {color: '#E91E63'}},
              {type: 'max', itemStyle: {color: '#2196F3'}}
            ]
          },
          markLine: {
            symbol: 'arrow',
            data: [
              {
                type: 'average',
                label: {
                  position: 'end',
                  formatter: 'Ave. {c}',
                  backgroundColor: '#FFEB3B',
                  color: '#000'
                }
              }
            ]
          }
        }}
        color={[green[500], red[500]]}
        height="30vh"
        exportData={data60352}
        exportFields={{
          lq_time: t('日付'),
          total_count: t('実績数'),
          ng: t('不良数'),
          ng_par: t('不良率')
        }}
        exportFilename={`${t('実績数と不良率')}`}
      />
    </ProgressAria>
  );
};

export default PerformanceAndDefectRateChart;
